import { faNetworkWired } from '@fortawesome/free-solid-svg-icons';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../hooks/useAxiosHook';
import { useEndpointGuard } from '../../../../hooks/useEndpointGuard';
import usePageTitle from '../../../../hooks/usePageTitle';
import useRouteDialog from '../../../../hooks/useRouteDialog';
import useTableColumns from '../../../../hooks/useTableColumns';
import useTableState from '../../../../hooks/useTableState';
import { WithData } from '../../../../types/api';
import { HubCollection, HubType } from '../../../../types/api/hubs';
import { EntityIdRouteParams } from '../../../../types/routing';
import { Unpacked } from '../../../../types/util';
import * as warehousesGuards from '../../../../utils/constants/auth/warehouses';
import {
  RoutePaths,
  constructIdRoute,
} from '../../../../utils/constants/routePaths';
import { queryString } from '../../../../utils/helpers/http';
import { httpQueryObject } from '../../../../utils/helpers/misc';
import Table from '../../../DataTable/Table/Table';
import Flex from '../../../layout/flex/Flex';
import MainContent from '../../../layout/flex/MainContent';
import Filters from '../../Components/Filters/Filters';
import HeaderPages from '../../Components/HeaderPages/HeaderPages';
import CreateEditDialog from './Dialogs/CreateEditDialog/CreateEditDialog';
import DeleteHubDialog from './Dialogs/Delete/DeleteHubDialog';
import ViewDialog from './Dialogs/ViewDialog/ViewDialog';
import useTableFilters from './useTableFilters';
import {
  additionalColumnProperties,
  getColumnHeadersMap,
  tableStorageKey,
} from './WarehousesList.functions';

function WarehousesList() {
  const { t } = useTranslation();
  const { id } = useParams<EntityIdRouteParams>();
  const [action, setAction] = useState<string>('');
  usePageTitle(t('Warehouses'));

  const readGuard = useEndpointGuard(warehousesGuards.readHub);
  const createGuard = useEndpointGuard(warehousesGuards.createHub);
  const editGuard = useEndpointGuard(warehousesGuards.editHub);
  const deleteGuard = useEndpointGuard(warehousesGuards.deleteHub);

  const {
    tableRef,
    page,
    setPage,
    limit,
    setLimit,
    sortField,
    sortOrder,
    selection,
    setSortField,
    setSortOrder,
    setSelection,
    contextMenuSelection,
    setContextMenuSelection,
  } = useTableState<Unpacked<HubCollection>>(tableStorageKey);

  const { headerFiltersCount, filters, resetAllFilters, httpFiltersObj } =
    useTableFilters(page, setPage!, limit);

  const columnHeadersMap = useMemo(() => getColumnHeadersMap(t), [t]);

  const { data, isLoading, reload, error } = useAxiosHook<HubCollection>(
    '/hubs' + queryString(httpQueryObject(httpFiltersObj))
  );

  const { selectedColumns, setSelectedColumns, columnOptions, columns } =
    useTableColumns(
      page,
      limit,
      'employees',
      columnHeadersMap,
      columnHeadersMap,
      (c) =>
        additionalColumnProperties(
          t,
          c as keyof typeof columnHeadersMap,
          setContextMenuSelection,
          setAction,
          readGuard,
          editGuard,
          deleteGuard
        )
    );

  const finalColumns = useMemo<JSX.Element[]>(
    () => [
      ...columns,
      <Column
        key="action-column"
        header={t('Actions')}
        field="actions"
        frozen
        alignFrozen="right"
        {...additionalColumnProperties(
          t,
          'actions',
          setContextMenuSelection,
          setAction,
          readGuard,
          editGuard,
          deleteGuard
        )}
      />,
    ],
    [columns, t, setContextMenuSelection, readGuard, editGuard, deleteGuard]
  );

  const {
    show: showViewDialog,
    hide: hideViewDialog,
    isVisible: isViewDialogVisible,
  } = useRouteDialog(
    RoutePaths.WarehousesList,
    RoutePaths.WareHouseView,
    readGuard
  );

  const {
    show: showAddDialog,
    hide: hideAddDialog,
    isVisible: isAddDialogVisible,
  } = useRouteDialog(
    RoutePaths.WarehousesList,
    RoutePaths.WareHouseCreate,
    createGuard
  );

  const {
    show: showEditDialog,
    hide: hideEditDialog,
    isVisible: isEditDialogVisible,
  } = useRouteDialog(
    RoutePaths.WarehousesList,
    constructIdRoute(RoutePaths.WareHouseEdit, id ?? contextMenuSelection?.id),
    editGuard
  );

  const {
    show: showDeleteDialog,
    hide: hideDeleteDialog,
    isVisible: isDeleteDialogVisible,
  } = useRouteDialog(
    RoutePaths.WarehousesList,
    constructIdRoute(
      RoutePaths.WarehouseDelete,
      id ?? contextMenuSelection?.id
    ),
    deleteGuard
  );

  const { data: hubData, isLoading: isHubDataLoading } = useAxiosHook<
    WithData<HubType>
  >(
    {
      url: `/hubs/${id ?? contextMenuSelection?.id}`,
    },
    {
      skipWhen:
        (!id && !contextMenuSelection?.id) ||
        (!isAddDialogVisible && !isEditDialogVisible && !isViewDialogVisible),
    }
  );

  function handleCreateEditHide() {
    hideEditDialog();
    hideAddDialog();
    hideViewDialog();
  }

  function handleFromViewToEdit() {
    hideViewDialog();
    hideAddDialog();
    showEditDialog();
  }

  useEffect(() => {
    if (action && contextMenuSelection) {
      if (action === 'view-details') {
        showViewDialog();
      }
      if (action === 'edit') {
        showEditDialog();
      }
      if (action === 'delete') {
        showDeleteDialog();
      }

      setAction('');
    }
  }, [
    action,
    contextMenuSelection,
    setAction,
    showViewDialog,
    showEditDialog,
    showDeleteDialog,
  ]);

  return (
    <div className="page hubs-page">
      <HeaderPages
        title={t('Warehouses')}
        subtitle={t('View and manage warehouses')}
        icon={faNetworkWired}
      >
        {createGuard && (
          <Button
            type="button"
            label={t('Create warehouse')}
            icon="fas fa-plus"
            className="main-btn"
            onClick={showAddDialog}
          />
        )}
      </HeaderPages>

      <ViewDialog
        visible={isViewDialogVisible}
        onHide={handleCreateEditHide}
        data={hubData?.data}
        isLoading={isHubDataLoading}
        {...(readGuard ? { onEdit: handleFromViewToEdit } : {})}
      />

      <CreateEditDialog
        visible={isAddDialogVisible || isEditDialogVisible}
        isLoading={isHubDataLoading}
        isEditDialog={isEditDialogVisible}
        data={hubData}
        onHide={handleCreateEditHide}
        reloadHubs={reload}
      />

      <DeleteHubDialog
        visible={isDeleteDialogVisible}
        onHide={() => hideDeleteDialog()}
        selection={contextMenuSelection}
        reloadHubLists={reload}
      />

      <Flex direction="column">
        <Filters
          filters={filters}
          resetAllFilters={resetAllFilters}
          headerFiltersCount={headerFiltersCount}
          filterHeight={180}
        />
        <MainContent>
          <Table
            tableRef={tableRef}
            columns={finalColumns}
            data={data}
            isLoading={isLoading}
            hasError={!!error}
            reload={reload}
            headerTitle=""
            setPage={setPage}
            setLimit={setLimit}
            sortField={sortField}
            rows={limit}
            filterHeight={180}
            setSortField={setSortField}
            setSortOrder={setSortOrder}
            setSelection={setSelection}
            sortOrder={sortOrder}
            selection={selection}
            storageString={tableStorageKey}
            rebuildTooltip
            selectedColumns={selectedColumns}
            setSelectedColumns={setSelectedColumns}
            columnOptions={columnOptions}
            exportToCSVButton
            onExportToCSVButtonClick={() =>
              (tableRef?.current as any)?.exportCSV()
            }
            contextMenuSelection={contextMenuSelection}
            setContextMenuSelection={setContextMenuSelection}
          />
        </MainContent>
      </Flex>
    </div>
  );
}
export default WarehousesList;
